import { Box, Chip, Stack, Tooltip, Typography } from '@mui/material';
import {
  DataGridPro,
  GridColDef,
  GridPaginationModel,
  GridRenderCellParams,
  GridRowClassNameParams,
  GridToolbarFilterButton,
  GridToolbarQuickFilter,
} from '@mui/x-data-grid-pro';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { getFullVoyageScheduleHistory } from '../../../../services/VoyageRepository';
import MBModal from '../../../../../../shared/components/modals/MBModal';
import { VoyageData } from '../../../../types/schedule.types';

interface VoyageRemarksModalProps {
  isModalOpen: boolean;
  onClose?: () => void;
  currentVoyageData: VoyageData;
}

interface HistoryEntry {
  date: string | number | Date | null;
  remark: string;
  changedBy: string | null;
}

type RemarkRow = {
  id: string;
  isCurrent: boolean;
} & HistoryEntry;

const QuickFilterToolbar = () => (
  <Stack
    direction='row'
    alignItems='center'
    justifyContent='space-between'
    p={1}
  >
    <GridToolbarFilterButton />
    <GridToolbarQuickFilter debounceMs={300} variant='outlined' size='small' />
  </Stack>
);

const VoyageRemarksModal = ({
  isModalOpen,
  onClose = () => undefined,
  currentVoyageData,
}: VoyageRemarksModalProps) => {
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    pageSize: 10,
    page: 0,
  });

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [voyageHistories, setVoyageHistories] = useState<VoyageData[]>([]);

  const {
    id: voyageId,
    generalRemarks: currentRemark,
    lastUpdatedAt: currentCreatedAt,
    lastUpdatedBy,
  } = currentVoyageData || {};

  const { emea: currentEditedBy } = lastUpdatedBy || {};

  const historyEntries = useMemo<HistoryEntry[]>(() => {
    const entriesWithRemark: HistoryEntry[] = voyageHistories
      .map((v) => ({
        date: v.lastUpdatedAt,
        remark: v.generalRemarks ?? '',
        changedBy: v.lastUpdatedBy?.emea ?? 'Unknown',
      }))
      .filter((e) => (e.remark ?? '').trim().length > 0);

    // Sort by date descending; entries without a date appear last
    const toMs = (d: HistoryEntry['date']) =>
      d ? dayjs(d).valueOf() : -Infinity;
    entriesWithRemark.sort((a, b) => toMs(b.date) - toMs(a.date));

    return entriesWithRemark;
  }, [voyageHistories]);

  const columns: GridColDef<RemarkRow>[] = useMemo(
    () => [
      {
        field: 'current',
        headerName: '',
        width: 100,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        cellClassName: 'current-chip-cell',
        renderCell: (params: GridRenderCellParams<RemarkRow, unknown>) =>
          params?.row?.isCurrent ? (
            <Chip
              label='Current'
              color='primary'
              size='small'
              variant='filled'
            />
          ) : null,
      },
      {
        field: 'date',
        headerName: 'Date',
        minWidth: 200,
        cellClassName: 'date-cell',
        renderCell: (
          params: GridRenderCellParams<RemarkRow, RemarkRow['date']>
        ) => (
          <Typography variant='caption' color='text.secondary'>
            {params?.row?.date ? dayjs(params.row.date).format('LLL') : ''}
          </Typography>
        ),
      },
      {
        field: 'remark',
        headerName: 'Remark',
        flex: 1,
        minWidth: 150,
        cellClassName: 'remark-cell',
        renderCell: (
          params: GridRenderCellParams<RemarkRow, RemarkRow['remark']>
        ) => (
          <Tooltip
            placement='left-start'
            enterDelay={2000}
            title={String(params.value ?? '')}
            arrow
          >
            <Box
              sx={{
                width: '100%',
                display: 'inline-flex',
                alignItems: 'center',
              }}
            >
              <Typography
                variant='caption'
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  display: 'block',
                  flex: 1,
                }}
              >
                {String(params.value ?? '')}
              </Typography>
            </Box>
          </Tooltip>
        ),
      },
      {
        field: 'changedBy',
        headerName: 'Changed By',
        minWidth: 160,
        cellClassName: 'changedBy-cell',
        renderCell: (
          params: GridRenderCellParams<RemarkRow, RemarkRow['changedBy']>
        ) => (
          <Typography variant='caption' color='text.secondary'>
            {String(params?.value ?? '')}
          </Typography>
        ),
      },
    ],
    []
  );

  const rows: RemarkRow[] = useMemo(() => {
    const historyRows: RemarkRow[] = historyEntries.map((item, idx) => ({
      id: `${idx}-${item.date}-${item.changedBy ?? ''}`,
      date: item.date,
      changedBy: item.changedBy,
      remark: item.remark,
      isCurrent: false,
    }));

    const currentText = currentRemark?.trim();
    if (!currentText) {
      return historyRows;
    }

    const currentRow: RemarkRow = {
      id: `current-${currentCreatedAt}-${currentEditedBy ?? ''}`,
      date: currentCreatedAt,
      changedBy: currentEditedBy,
      remark: currentText || '',
      isCurrent: true,
    };

    return [currentRow, ...historyRows];
  }, [historyEntries, currentRemark, currentEditedBy, currentCreatedAt]);

  useEffect(() => {
    if (!isModalOpen || !voyageId) return;
    const fetchHistory = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await getFullVoyageScheduleHistory(voyageId);
        setVoyageHistories(data);
      } catch (e: unknown) {
        const message = (e as any)?.message || 'Failed to load remarks history';
        setError(String(message));
      } finally {
        setIsLoading(false);
      }
    };
    fetchHistory();
  }, [isModalOpen, voyageId]);

  const onModalClose = () => {
    onClose();
  };

  return (
    <MBModal
      open={isModalOpen}
      onClose={onModalClose}
      title='Voyage Remarks History'
      isLoading={isLoading}
      maxWidth='md'
      fullWidth
    >
      <Stack spacing={1.5}>
        <Box>
          {error ? (
            <Typography color='error'>{error}</Typography>
          ) : rows.length === 0 ? (
            <Typography variant='body2' color='text.secondary'>
              No history available
            </Typography>
          ) : (
            <DataGridPro
              rows={rows}
              columns={columns}
              slots={{ toolbar: QuickFilterToolbar }}
              disableColumnMenu
              density='compact'
              pagination
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              pageSizeOptions={[5, 10, 25]}
              getRowId={(row) => row.id}
              getRowClassName={(params: GridRowClassNameParams<RemarkRow>) =>
                params.row.isCurrent ? 'current-row' : ''
              }
              hideFooterSelectedRowCount
              disableRowSelectionOnClick
              sx={{
                '& .MuiDataGrid-columnHeaders': {
                  fontSize: '0.875rem',
                  lineHeight: 1.25,
                },
                '& .MuiDataGrid-cell': {
                  fontSize: '0.875rem',
                },
                '& .date-cell, & .changedBy-cell, & .remark-cell': {
                  alignItems: 'center',
                },
                '& .current-chip-cell': {
                  alignItems: 'center',
                },
                '& .current-row': {
                  backgroundColor: (theme) => theme.palette.action.hover,
                  boxShadow: (theme) =>
                    `inset 3px 0 0 ${theme.palette.primary.main}`,
                },
              }}
            />
          )}
        </Box>
      </Stack>
    </MBModal>
  );
};

export default VoyageRemarksModal;
